import Immutable from 'immutable';
import { get } from './get';
import { pageContext } from './page_context';
import { buildUrlWithParams } from './url';

/**
 * Action Creator for api actions. Set the type, url and apiSchema from the apiSchema.
 * @param {Object} apiSchema - A specific api schema for a page.
 * @param {Object} pageState - Tightly coupled to PageWrapper mapStateToProps(), it has all page settings.
 * @param {Object} pageState.settings - Contains page settings.
 * @param {Object} pageState.settings.vertical - ???.
 * @param {Object} pageState.settings.qualifier - ???.
 * @param {Immutable.Map} pageState.serverConfig - Used to find baseUrl.
 * @param {Array} restArgs - An array of pathSegments which could be presented in 2 different ways
 *                           1. static paths (e.g. ['new', 'ctm'] = '/new/ctm')
 *                           2. paths with value  (e.g. [{ "varPath": "new", "value": "ctm" }] = '/new/ctm')
 *
 *                           Note: if your restArgs uses state store identifier, it will need to mapped beforehand.
 * @param {String} baseUrlName - Used in conjunction with pageState.serverConfig to get baseUrl.
 * @param {Object} requestPayload - The payload for the request. optional, if not provided, the payload is prepared by saga
 * @returns {Object} An action that describes an api call to the backend.
 */

export const apiAction = (apiSchema, pageState, restArgs = [], baseUrlName = 'journeyServiceBaseUrl', requestPayload) => {
    const verticalStateKey = pageContext.getVertical();
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    return {
        type: apiSchema.requestAction, // todo: why is this here, when it is also inside apiSchema, which is returned as well? (Jose?)
        url: buildUrl(apiSchema, get(pageState, `serverConfig.${baseUrlName}`), restArgs),
        verticalStateKey,
        qualifier: settings.qualifier,
        apiSchema,
        restArgs, // todo: should this be returned? its already inside restArgs?
        pageState,

        // Optional, need to be prepared by caller,
        // if not provided, it will use apiSchema.requestPayload which will get prepared by Saga
        // if both apiSchema.requestPayload and action.requestPayload are provided
        // PROCESS_PAYLOAD saga will use apiSchema.requestPayload then take action.requestPayload as extraPayload
        // Still up to each individual selector to implement the extraPayload though
        requestPayload
    };
};

/**
 * Build a fully qualified URL using the apiSchema and any overrides passed in.
 * @param {Object} apiSchema - A specific api schema for a page.
 * @param {String} apiSchema.baseUrl - Essentially protocol://domainname/.
 * @param {String} apiSchema.url - A path to add to baseUrl.
 * @param {string[]} apiSchema.restArgs - An array of pathSegments (e.g. ['new', 'ctm']).
 * @param {String} baseUrlOverride - Used to override apiSchema.baseUrl.
 * @param {string[]} restArgsOverride - Used to override apiSchema.restArgs.
 * @throws {Error} Invalid arguments or when a URL cannot be built.
 * @returns {String} The fully qualified URL.
 */
export const buildUrl = (apiSchema = {}, baseUrlOverride, restArgsOverride) => {
    // Build URL parts
    const baseUrl = baseUrlOverride || apiSchema.baseUrl || '';
    // Throw if URL cannot be generated
    if (!baseUrl) {
        throw new Error('Unable to create a URL with the arguments passed to buildUrl()');
    }

    const path = apiSchema.url || '';
    const queryStringArgs = getQueryStringArgs(apiSchema.queryStringValues) || false;
    const pathFromSchema = buildAdditionalPath(apiSchema.restArgs);
    // todo: why do we have these two overrides?
    const pathFromArgsOverride = buildAdditionalPath(restArgsOverride);
    const additionalPath = pathFromArgsOverride || pathFromSchema;

    // Put URL together.
    // We don't know if the parts have a leading or trailing slash, so we add a
    // slash between the URL parts and then strip out any duplicate slashes,
    // except after the protocol (e.g. http://...).
    let url = `${baseUrl}/${path}`;
    if (additionalPath) {
        url = `${url}/${additionalPath}`;
    }

    if (queryStringArgs) {
        url = buildUrlWithParams(url, queryStringArgs);
    }

    return url.replace(/([^:]\/)\/+/g, '$1');
};

/**
 * Generate arguments necessary for buildUrlWithParams
 * @param {Array} queryStringValues An array of [{varPath: 'key', identifier: 'state.key' }] or in place of identifier, a static value: 'somevalue'
 * @returns {Object} A mapping of key=value
 */
export const getQueryStringArgs = (queryStringValues) => {
    if (!queryStringValues) return '';
    // Throw error if queryString is not an array.
    if (!Array.isArray(queryStringValues)) {
        const objType = Object.prototype.toString.call(queryStringValues);
        throw new Error(`buildQueryStringArgs() accepts an Array as argument. ${objType} was passed instead`);
    }
    // Convert it to the key/value format buildUrl expects.
    return queryStringValues.reduce((acc, curr) => {
        acc[encodeURIComponent(curr.varPath)] = encodeURIComponent(curr.value);
        return acc;
    }, {});
};
/**
 * Build Additional path for the passed restful Arguments using its varPath and value.
 * @param  {Array} restArgs - An array of RESTful Arguments.
 * @returns {String} path to be added to the URL.
 */
export const buildAdditionalPath = (restArgs) => {
    if (!restArgs) return null;

    // Throw error if restArgs is not an array.
    if (!Array.isArray(restArgs)) {
        const objType = Object.prototype.toString.call(restArgs);
        throw new Error(`buildAdditionalPath() accepts an Array as argument. ${objType} was passed instead`);
    }

    // If queryParams obj then insert as a query paramater
    // Regex to remove any '/' that occur before a '?' or '&'
    // Note: Will not work for query params seperated by an additional parameter
    let queryParamCount = 0;
    return restArgs.filter((obj) => {
        return obj.value;
    }).map((obj) => {
        if (obj.queryParam) {
            const separator = queryParamCount === 0 ? '?' : '&';
            queryParamCount += 1;
            return `${separator}${obj.queryParam}=${obj.value}`;
        }
        return obj.varPath ? `${encodeURIComponent(obj.varPath)}/${encodeURIComponent(obj.value)}` : obj.value;
    }).join('/').replace(/\/(?=[?&])/g, '');
};
